import { useContext, useMemo } from 'react';
import type React from 'react';
import { useIsMobile } from '~/contexts/DeviceContext';
import SuggestionsContext from '~/contexts/SuggestionsContext';
import { Suggestion } from '~/contracts';
import ClockIcon from '~/icons/ClockIcon';
import HeartIcon from '~/icons/HeartIcon';
import useSavedSearches, { type SavedSearchWithText } from './useSavedSearches';
import { SearchIcon } from '../../KeywordSearch.styles';
import ClearAll from '../ClearAll';
import { isButton } from '../utils';

export type SuggestionButton = {
  element: React.ReactElement;
  onSelect: () => void;
};

export type SuggestionItem =
  | SavedSearchWithText
  | SuggestionButton
  | Suggestion;

export type SuggestionSection = {
  title: string;
  titleIcon: React.SVGProps<SVGSVGElement> | null;
  clearable: boolean;
  suggestions: SuggestionItem[];
};

const escapeRegex = (str: string): string =>
  /* eslint-disable-next-line */
  str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');

const highlightMatchedText =
  (value: string) =>
  (suggestion: SuggestionItem): SuggestionItem => {
    if (isButton(suggestion)) {
      return suggestion;
    }

    const regex = new RegExp(escapeRegex(value), 'i');
    suggestion.highlightedText = suggestion.text?.replace(
      regex,
      replaced => `<span class="font-bold">${replaced}</span>`
    );

    return suggestion;
  };

const suggestionIncludesValue =
  (value: string) =>
  (suggestion: SuggestionItem): boolean =>
    isButton(suggestion)
      ? true
      : suggestion.text?.toLowerCase()?.includes(value.toLowerCase()) ?? false;

type OwnArgs = {
  clearAllSavedSuggestions: () => void;
  shouldRenderSuggestions: boolean;
  value: string;
};

export default function useSuggestionSections({
  clearAllSavedSuggestions,
  shouldRenderSuggestions,
  value
}: OwnArgs): SuggestionSection[] {
  const isMobile = useIsMobile();
  const {
    savedSuggestions,
    suggestions: { data: suggestionsData }
  } = useContext(SuggestionsContext);
  const savedSearches = useSavedSearches();

  return useMemo(() => {
    if (!shouldRenderSuggestions) {
      return [];
    }

    const sections: SuggestionSection[] = [];

    // saved searches
    const filteredOrderedSavedSearches = savedSearches.filter(
      suggestionIncludesValue(value)
    );

    if (filteredOrderedSavedSearches.length > 0) {
      sections.push({
        title: 'Saved Searches',
        titleIcon: <HeartIcon isSavedSearch={false} />,
        clearable: false,
        suggestions: filteredOrderedSavedSearches.map(
          highlightMatchedText(value)
        )
      });
    }

    // saved suggestions
    const filteredSavedSuggestions = savedSuggestions.filter(
      suggestionIncludesValue(value)
    );

    const savedSuggestionsToShow = filteredSavedSuggestions.slice(
      0,
      value ? 3 : 10
    );

    if (savedSuggestionsToShow.length > 0) {
      sections.push({
        title: 'Your Recent Searches',
        titleIcon: <ClockIcon />,
        clearable: true,
        suggestions: savedSuggestionsToShow.map(highlightMatchedText(value))
      });

      if (!isMobile) {
        sections.push({
          title: '',
          titleIcon: null,
          clearable: false,
          suggestions: [
            {
              element: <ClearAll onClick={clearAllSavedSuggestions} />,
              onSelect: clearAllSavedSuggestions
            }
          ]
        });
      }
    }

    // keyword search results
    if (suggestionsData.length > 0 && value) {
      sections.push({
        title: 'Suggestions',
        titleIcon: <SearchIcon className="grey-fill" />,
        clearable: false,
        suggestions: suggestionsData.map(highlightMatchedText(value))
      });
    }

    return sections;
  }, [
    clearAllSavedSuggestions,
    isMobile,
    savedSearches,
    savedSuggestions,
    shouldRenderSuggestions,
    suggestionsData,
    value
  ]);
}
